import { useLayoutEffect, type DependencyList, type RefObject } from 'react';

/** Строка растягивается, если чипы своей ширины занимают хотя бы эту долю. */
const FILL_SHARE = 0.5;

/**
 * Растягивание строк flex-wrap контейнера «как текст по ширине» (решение владельца
 * 2026-09-26). Чипы тянутся (flex-grow), чтобы справа не оставалось пустого места, но
 * одиночный чип и полупустая строка («седеть», «сереть» во всю ширину) выглядят как
 * кнопки. Поэтому строка растягивается, только если в ней больше одного чипа и они
 * своей ширины уже занимают ≥ FILL_SHARE; иначе чипам ставится `data-natural`, и
 * стиль снимает с них flex-grow. Рост не меняет перенос строк — раскладка та же.
 *
 * itemSelector — какие дети считаются чипами: служебные кнопки в строке («свернуть»,
 * «показать ещё») не растягиваются и в расчёт не входят.
 */
export function useLineFill(
  ref: RefObject<HTMLElement | null>,
  deps: DependencyList,
  itemSelector?: string,
) {
  useLayoutEffect(() => {
    const box = ref.current;
    if (!box) return;
    const mark = () => {
      const items = (Array.from(box.children) as HTMLElement[]).filter(
        (el) => !itemSelector || el.matches(itemSelector),
      );
      // Сначала все своей ширины — меряем натуральные размеры.
      for (const el of items) el.setAttribute('data-natural', '');
      const width = box.clientWidth;
      const gap = parseFloat(getComputedStyle(box).columnGap) || 0;
      const lines = new Map<number, HTMLElement[]>();
      for (const el of items) {
        const line = lines.get(el.offsetTop);
        if (line) line.push(el);
        else lines.set(el.offsetTop, [el]);
      }
      for (const line of lines.values()) {
        const natural =
          line.reduce((sum, el) => sum + el.offsetWidth, 0) + gap * (line.length - 1);
        const stretch = line.length > 1 && width > 0 && natural >= width * FILL_SHARE;
        for (const el of line) el.toggleAttribute('data-natural', !stretch);
      }
    };
    mark();
    const observer = new ResizeObserver(mark);
    observer.observe(box);
    return () => observer.disconnect();
    // deps задаёт вызывающий: состав и состояние чипов.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
